import { create } from 'zustand';
import type { Node as ReactFlowNode, Edge as ReactFlowEdge } from 'reactflow';

interface Snapshot {
  nodes: ReactFlowNode[];
  edges: ReactFlowEdge[];
}

interface HistoryState {
  past: Snapshot[];
  future: Snapshot[];
  maxHistory: number;

  pushSnapshot: (snapshot: Snapshot) => void;
  undo: (currentState: Snapshot) => Snapshot | null;
  redo: (currentState: Snapshot) => Snapshot | null;
  canUndo: () => boolean;
  canRedo: () => boolean;
  clear: () => void;
}

export const useHistoryStore = create<HistoryState>((set, get) => ({
  past: [],
  future: [],
  maxHistory: 50,

  // Called BEFORE a mutation: saves the current state so we can go back to it
  pushSnapshot: (snapshot: Snapshot) => {
    const { past, maxHistory } = get();
    const newPast = [...past, snapshot].slice(-maxHistory);
    set({ past: newPast, future: [] });
  },

  // Undo: pop from past, push current into future, return the state to restore
  undo: (currentState: Snapshot) => {
    const { past, future } = get();
    if (past.length === 0) return null;

    const restored = past[past.length - 1];
    set({
      past: past.slice(0, -1),
      future: [currentState, ...future],
    });
    return restored;
  },

  // Redo: pop from future, push current into past, return the state to restore
  redo: (currentState: Snapshot) => {
    const { past, future } = get();
    if (future.length === 0) return null;

    const restored = future[0];
    set({
      past: [...past, currentState],
      future: future.slice(1),
    });
    return restored;
  },

  canUndo: () => get().past.length > 0,
  canRedo: () => get().future.length > 0,

  clear: () => set({ past: [], future: [] }),
}));

export default useHistoryStore;
